import React, { useEffect } from "react";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import {
  BsCheckCircleFill,
  BsClockHistory,
  BsFileEarmarkText,
  BsGeoAlt,
  BsCalendar3,
  BsMoon,
  BsPeople,
  BsPerson,
  BsCreditCard,
  BsBuilding,
  BsStarFill,
  BsHouse,
  BsShieldLock,
  BsCheck2Circle,
  BsClock,
  BsAirplane,
} from "react-icons/bs";

import { useAppContext } from "../../../contexts/AppContext";
import { formatUsdDisplayInteger } from "../../../utils/ceilUsd";
import { isAdminUser } from "../../../services/authService";
import { shortClockFromLdsDateTime } from "../../FlightBookingFlowMockup/adapters/flightOfferAdapter";
import { FlightLegBlock } from "../../FlightBookingFlowMockup/flightBookingMockup/FlightOfferResultCardLayout.jsx";
import "../../FlightBookingFlowMockup/flightBookingMockup/FlightOfferResultCard.css";
import LdsCustomerQuoteLegalSection from "../../InfoBox/LdsCustomerQuoteLegalSection";
import PublicOfferIncludedBaggageCard from "../../Client/PublicOffer/PublicOfferIncludedBaggageCard";
import PublicOfferIncludedTransfersCard from "../../Client/PublicOffer/PublicOfferIncludedTransfersCard";
import WhatsAppIcon from "../../../assets/images/icons/whatsapp.png";
import {
  buildCustomerOfferWhatsAppShareMessage,
  buildWhatsAppShareUrl,
} from "../../../utils/whatsappDeepLink";
import CustomerQuoteAgencyContactFooter from "../../InfoBox/CustomerQuoteAgencyContactFooter";
import {
  isBankTransferAwaitingPayment,
} from "../../../utils/ldsOrderStatus";
import {
  AGENT_DASHBOARD_HOME_PATH,
  logBankTransferSuccessCtaTrace,
  resolveAgentDashboardBankTransferSuccessCta,
} from "../../../utils/orders/resolveAgentDashboardLdsBankTransferSuccess";
import LdsBankTransferDetailsBlock from "./LdsBankTransferDetailsBlock";
import {
  LDS_BANK_TRANSFER_PENDING_BADGE_HE,
  LDS_BANK_TRANSFER_SUCCESS_SUBTITLE_HE,
  LDS_BANK_TRANSFER_SUCCESS_TITLE_HE,
} from "../../../utils/ldsBankTransferDetails";
import { ordersDashboardCategoryPath } from "../../../utils/ordersDashboardRoutes";
import { resolveOrganizedTourDisplayOrderNumber } from "../../../utils/resolveOrganizedTourDisplayOrderNumber";
import isEqual from "lodash/isEqual";
import { useDeepCompareMemo } from "../../../hooks/useDeepCompareMemo";
import { safeOrderSummaryTrace } from "../../../utils/safeOrderSummaryTrace";
import "./OrderSummary.scss";

const getDetail = (details, title) =>
  details?.find((d) => d.title === title)?.value ?? "-";

const HE_WEEKDAYS = ["ראשון", "שני", "שלישי", "רביעי", "חמישי", "שישי", "שבת"];

/** Parses date string (DD/MM/YYYY or ISO) and returns "DD/MM/YYYY (weekday)" in Hebrew, or original string if unparseable */
function formatDateWithHebrewWeekday(dateStr) {
  if (!dateStr || dateStr === "-" || typeof dateStr !== "string") return dateStr;
  const trimmed = dateStr.trim();
  let date;
  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
    date = new Date(trimmed);
  } else {
    const parts = trimmed.split("/").map((p) => parseInt(p, 10));
    if (parts.length === 3 && parts.every((n) => !Number.isNaN(n))) {
      const [d, m, y] = parts;
      if (parts[0] > 31) date = new Date(parts[0], parts[1] - 1, parts[2]);
      else date = new Date(y, m - 1, d);
    } else {
      date = new Date(trimmed);
    }
  }
  if (Number.isNaN(date.getTime())) return dateStr;
  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  const weekday = HE_WEEKDAYS[date.getDay()] || "";
  const formatted = `${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}/${year}`;
  return weekday ? `${formatted} (${weekday})` : formatted;
}

/** הסרת סיומת " (ראשון)" ממחרוזת תאריך לפני פרסור */
function stripHebrewWeekdaySuffix(dateStr) {
  if (!dateStr || typeof dateStr !== "string") return dateStr;
  return dateStr.replace(/\s*\([^)]*\)\s*$/, "").trim();
}

/**
 * DD/MM/YYYY או ISO → תאריך מקומי. null אם לא ניתן לפרסר.
 * (אותה לוגיקה כמו formatDateWithHebrewWeekday)
 */
function parseSummaryDateToLocal(dateStr) {
  if (!dateStr || dateStr === "-" || typeof dateStr !== "string") return null;
  const trimmed = stripHebrewWeekdaySuffix(dateStr);
  let date;
  if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
    date = new Date(trimmed);
  } else {
    const parts = trimmed.split("/").map((p) => parseInt(p, 10));
    if (parts.length === 3 && parts.every((n) => !Number.isNaN(n))) {
      const [d, m, y] = parts;
      if (parts[0] > 31) date = new Date(parts[0], parts[1] - 1, parts[2]);
      else date = new Date(y, m - 1, d);
    } else {
      date = new Date(trimmed);
    }
  }
  if (Number.isNaN(date.getTime())) return null;
  return date;
}

/** מספר לילות במלון = הפרש ימים בין עזיבה לכניסה (למשל 19/04–23/04 → 4) */
function normalizeIataCarrier(carrier) {
  const s = String(carrier || "")
    .trim()
    .toUpperCase();
  if (/^[A-Z]{2,3}$/.test(s)) return s;
  const letters = s.replace(/[^A-Z]/g, "");
  if (letters.length >= 2) return letters.slice(0, 3);
  const alnum = s.replace(/[^A-Z0-9]/g, "");
  return alnum.length >= 2 ? alnum.slice(0, 3) : s.slice(0, 3);
}

/** שעות יציאה/הגעה לשורת סיכום חבילה (מקטע בודד) */
function hhmmFromOrderFlight(flight) {
  const tryDayjs = (raw) => {
    if (raw == null || String(raw).trim() === "") return null;
    const d = dayjs(String(raw).trim());
    return d.isValid() ? d.format("HH:mm") : null;
  };
  const depCombined = [flight.depDate, flight.depTime].filter(Boolean).join(" ").trim();
  let dep = tryDayjs(depCombined);
  if (!dep && flight.depTime) {
    const t = String(flight.depTime).trim();
    const m = t.match(/(\d{1,2}):(\d{2})/);
    dep = m
      ? `${String(parseInt(m[1], 10)).padStart(2, "0")}:${m[2]}`
      : tryDayjs(t) || t || null;
  }
  if (!dep && flight.dateTime) {
    const m = String(flight.dateTime).match(/(\d{1,2}):(\d{2})/);
    dep = m ? `${String(parseInt(m[1], 10)).padStart(2, "0")}:${m[2]}` : null;
  }
  const arrCombined = [flight.arrDate || flight.depDate, flight.arrTime].filter(Boolean).join(" ").trim();
  let arr = tryDayjs(arrCombined);
  if (!arr && flight.arrTime) {
    const t = String(flight.arrTime).trim();
    const m = t.match(/(\d{1,2}):(\d{2})/);
    arr = m
      ? `${String(parseInt(m[1], 10)).padStart(2, "0")}:${m[2]}`
      : tryDayjs(t) || t || null;
  }
  /* שעה לעיתים רק בתוך arrDate/depDate (LDS), בלי שדה נפרד */
  if (!dep && flight.depDate) {
    const c = shortClockFromLdsDateTime(flight.depDate);
    if (c) dep = c;
  }
  if (!arr && flight.arrDate) {
    const c = shortClockFromLdsDateTime(flight.arrDate);
    if (c) arr = c;
  }
  return { depShown: dep || "—", arrShown: arr || "—" };
}

/** DD/MM/YYYY לתצוגה כמו בכרטיס תוצאות (fpc__leg-date) */
function formatDdMmYyyyFromFlightRaw(raw) {
  if (raw == null || String(raw).trim() === "") return null;
  const s = String(raw).trim();
  const d = dayjs(s);
  if (d.isValid()) return d.format("DD/MM/YYYY");
  const local = parseSummaryDateToLocal(s);
  if (local) {
    return `${String(local.getDate()).padStart(2, "0")}/${String(local.getMonth() + 1).padStart(2, "0")}/${local.getFullYear()}`;
  }
  const m = s.match(/\d{1,2}\/\d{1,2}\/\d{4}/);
  return m ? m[0] : null;
}

function isoDateAttrFromRaw(raw) {
  if (raw == null || String(raw).trim() === "") return undefined;
  const s = String(raw).trim();
  const d = dayjs(s);
  if (d.isValid()) return d.format("YYYY-MM-DD");
  const local = parseSummaryDateToLocal(s);
  return local ? dayjs(local).format("YYYY-MM-DD") : undefined;
}

/** משך H:MM ל־FlightLegBlock (כמו flightOfferAdapter) */
function tryDurationHmFromPackageFlight(flight) {
  const depStr = [flight.depDate, flight.depTime].filter(Boolean).join(" ").trim();
  const arrStr = [flight.arrDate || flight.depDate, flight.arrTime].filter(Boolean).join(" ").trim();
  if (!depStr || !arrStr) return null;
  const dep = dayjs(depStr);
  const arr = dayjs(arrStr);
  if (!dep.isValid() || !arr.isValid()) return null;
  let mins = arr.diff(dep, "minute");
  if (mins <= 0 || mins > 48 * 60) return null;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}:${String(m).padStart(2, "0")}`;
}

function resolveFlightNumberForFacts(flight) {
  if (flight.flightNumber != null && String(flight.flightNumber).trim() !== "") {
    return String(flight.flightNumber).trim();
  }
  if (flight.flightCode) {
    const fc = String(flight.flightCode).trim();
    const m = fc.match(/^([A-Za-z]{2,3})(\d+)$/);
    if (m) return m[2];
    return fc;
  }
  return null;
}

function buildPackageRouteHeading(flights) {
  if (!flights?.length) return null;
  const first = flights[0];
  const last = flights[flights.length - 1];
  const o1 = first.depCode ? String(first.depCode).trim() : "";
  const d1 = first.arrCode ? String(first.arrCode).trim() : "";
  const o2 = last.depCode ? String(last.depCode).trim() : "";
  const d2 = last.arrCode ? String(last.arrCode).trim() : "";
  if (o1 && d1 && o2 && d2 && o1 === d2 && d1 === o2) {
    return `${o1} ↔ ${d1}`;
  }
  if (first.route && String(first.route).trim()) {
    return String(first.route).trim().replace(/-/g, " → ");
  }
  if (o1 && d1) return `${o1} → ${d1}`;
  return null;
}

function toRoomOccupancyLabel(roomEntry) {
  if (!roomEntry || typeof roomEntry !== "object") return "—";
  const adults = Array.isArray(roomEntry.adults) ? roomEntry.adults.length : 0;
  const kids = Array.isArray(roomEntry.kids) ? roomEntry.kids.length : 0;
  const infants = Array.isArray(roomEntry.infants) ? roomEntry.infants.length : 0;
  const parts = [];
  if (adults > 0) parts.push(`${adults} ${adults === 1 ? "מבוגר" : "מבוגרים"}`);
  if (kids > 0) parts.push(`${kids} ${kids === 1 ? "ילד" : "ילדים"}`);
  if (infants > 0) parts.push(`${infants} ${infants === 1 ? "תינוק" : "תינוקות"}`);
  return parts.length ? parts.join(" · ") : "—";
}

function buildRoomOccupancies(orderReqData, roomsCount, fallbackRooms = []) {
  const src =
    orderReqData?.passengersinfo ??
    orderReqData?.passengers_info ??
    orderReqData?.passengersInfo;
  const out = [];
  if (Array.isArray(src) && src.length > 0) {
    const desiredCount = Number.isFinite(Number(roomsCount)) && Number(roomsCount) > 0
      ? Number(roomsCount)
      : src.length;
    for (let i = 0; i < desiredCount; i += 1) {
      out.push(toRoomOccupancyLabel(src[i]));
    }
    return out;
  }
  const fallbackCount = Number.isFinite(Number(roomsCount)) && Number(roomsCount) > 0
    ? Number(roomsCount)
    : Array.isArray(fallbackRooms) && fallbackRooms.length > 0
      ? fallbackRooms.length
      : 1;
  for (let i = 0; i < fallbackCount; i += 1) out.push("—");
  return out;
}

/** מיפוי flightDetails מ־PackageOrderSummary לצורת leg של כרטיס תוצאות */
function mapPackageFlightDetailToFpcLeg(flight, index, totalLegs) {
  const iata = normalizeIataCarrier(flight.carrier);
  const depCode = flight.depCode ? String(flight.depCode).trim() : "";
  const arrCode = flight.arrCode ? String(flight.arrCode).trim() : "";
  const { depShown, arrShown } = hhmmFromOrderFlight(flight);
  const depDateDisplay =
    formatDdMmYyyyFromFlightRaw(flight.depDate) ||
    (flight.dateTime && typeof flight.dateTime === "string"
      ? (flight.dateTime.match(/\d{1,2}\/\d{1,2}\/\d{4}/) || [])[0]
      : null);

  let labelHe = "טיסה";
  if (totalLegs > 1) {
    if (index === 0) labelHe = "הלוך";
    else if (index === 1) labelHe = "חזור";
    else labelHe = `מקטע ${index + 1}`;
  }

  const duration = tryDurationHmFromPackageFlight(flight);

  return {
    id: `pkg-order-flight-${index}`,
    labelHe,
    originCode: depCode || "—",
    destinationCode: arrCode || "—",
    departureTime: depShown,
    arrivalTime: arrShown,
    depDate: isoDateAttrFromRaw(flight.depDate),
    arrDate: isoDateAttrFromRaw(flight.arrDate || flight.depDate),
    depDateDisplay,
    duration,
    isDirect: true,
    stopLabelHe: "עם עצירות",
    airlineCode: iata || null,
    flightNumber: resolveFlightNumberForFacts(flight),
    supplierCode: flight.supplierCode || flight.supplier || null,
    flightCategory: null,
  };
}

function computeNightsFromCheckInOut(checkInStr, checkOutStr) {
  const dIn = parseSummaryDateToLocal(checkInStr);
  const dOut = parseSummaryDateToLocal(checkOutStr);
  if (!dIn || !dOut) return null;
  const days = Math.round((dOut.getTime() - dIn.getTime()) / 86400000);
  if (days <= 0 || days > 366) return null;
  return days;
}

/** ערך "מספר לילות" מה-details, או חישוב מתאריכים אם חסר/לא תקין */
function resolveNightsDisplay(details) {
  const raw = getDetail(details, "מספר לילות");
  const checkIn = getDetail(details, "כניסה");
  const checkOut = getDetail(details, "עזיבה");
  const match = raw != null && raw !== "-" ? String(raw).match(/\d+/) : null;
  const parsed = match ? parseInt(match[0], 10) : NaN;
  const hasValidFromApi =
    raw != null &&
    raw !== "-" &&
    String(raw).trim() !== "" &&
    Number.isFinite(parsed) &&
    parsed > 0;
  if (hasValidFromApi) return String(parsed);
  const fromDates = computeNightsFromCheckInOut(checkIn, checkOut);
  if (fromDates != null && fromDates > 0) return String(fromDates);
  return raw ?? "-";
}

const OrderSummary = ({
  loading,
  hotelCountry,
  hotelname,
  hotelMainImage,
  fullname,
  details,
  orderReqData = {},
  rating,
  roomType,
  board,
  passengersList = [],
  roomsCount: roomsCountProp,
  pricePerRoom: pricePerRoomProp,
  roomNamesList = [],
  totalPriceUsd,
  paymentLink,
  onProceedToPayment,
  pendingOfferPaymentContext = null,
  baggageDetails = null,
  hasCityTax,
  customerQuoteOffer = false,
  customerQuoteOfferNumber = null,
  customerQuotePriceDisclaimer = null,
  /** HTML מידע נוסף (כמו בעמוד פרטי חבילה) — רק בהצעת מחיר אישית */
  customerQuoteAdditionalInfoHtml = "",
  /** לוגו + פרטי סוכנות מ-order.agent (אותו לוגו כמו בניהול) */
  customerQuoteAgencyBranding = null,
  /** טקסט פתיחה ללקוח; אם ריק — ניסוח ברירת מחדל */
  customerQuoteIntroText = null,
  /** שם פנייה בהצעת מחיר (?offer=1) — "שלום {שם}," או "שלום" */
  customerQuoteGreetingName = null,
  /** When false (e.g. /packages/profile/:id): never show green "מאושרת" success header — use proposal/draft styling only */
  allowSuccessHeader = true,
  /** מסך אישור מלא אחרי רישום העברה בנקאית (נתיב success) */
  bankTransferSubmittedReceipt = false,
  /** `/p/dashboard/success/:orderId` — רק כפתור "לעמוד הבית", בלי CTAs תשלום */
  agentDashboardBankTransferSuccess = false,
  /** כותרת גיבורי — ברירת מחדל `hotelname` (למשל שם טיול מאורגן) */
  heroDisplayName = null,
  /** דירוג כוכבים — רלוונטי בעיקר לחבילות מלון */
  showHeroStarRating = true,
  /** הסתרת בלוק "פירוט חדרים" (טיול מאורגן בלי מלון) */
  hideRoomDetailsSection = false,
  /** כותרת כרטיס פרטי חבילה (ברירת מחדל: פרטי החבילה) */
  packageFactsSectionTitle = null,
  /** דריסת כותרת סטטוס בכותרת העליונה */
  statusHeaderTitleOverride = null,
  /** דריסת תת־כותרת סטטוס */
  statusHeaderSubtitleOverride = null,
  /** טקסטים שמזכירים "תשלום במלון" מותאמים לטיול מאורגן */
  organizedTourPriceFootnote = false,
  /** אם ממולא — מחליף את שורות מבוגרים/ילדים בשורת "נוסעים" אחת */
  packageGuestsSingleLine = null,
  /** פרטי טיסה - מערך של אובייקטים עם carrier, flightNumber, dateTime, airlineName, airlineLogo */
  flightDetails = null,
  /** בלוק „מה כלול בחבילה” — מוצג מתחת לפרטי החבילה/טיסה ומעל מחיר לפי הצעה */
  includedAttractionsSlot = null,
  /** כבודה כלולה — כרטיס בולט בהצעת מחיר ללקוח (?offer=1) */
  publicOfferIncludedBaggageVm = null,
  /** העברות כלולות — כרטיס בולט בהצעת מחיר ללקוח (?offer=1) */
  publicOfferIncludedTransfersVm = null,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAppLoggedIn } = useAppContext();
  /** נתיב success ייעודי ללקוח — לא הצעת מחיר, גם בממתין להעברה בנקאית אחרי אישור */
  const isPackagesProfileDedicatedSuccessPath = /^\/packages\/profile\/[^/]+\/success\/?$/.test(
    location.pathname || ""
  );
  const isBankTransferRail = orderReqData?.bank_transfer === true;
  const bankAwaitingPayment = isBankTransferAwaitingPayment(orderReqData);
  const bankTransferSuccessCta = resolveAgentDashboardBankTransferSuccessCta(orderReqData, {
    agentDashboardBankTransferSuccess,
    pathname: location.pathname,
  });
  const isAgentDashboardBankTransferSuccessMode = bankTransferSuccessCta.showOnlyHomeButton;
  const paymentLinkUrl = typeof paymentLink === "string" && paymentLink.trim() ? paymentLink.trim() : null;
  /**
   * טרום־תשלום / טיוטה מ־/packages/profile/:id — כמו ב־PackageOrderSummary כש־!isLdsOrderBookingConfirmed.
   * לא להציג ניסוח "מחיר סופי" / "ההזמנה אושרה"; כן להציג CTA להמשך תשלום (או לינק תשלום).
   */
  const isProposalFlow = allowSuccessHeader === false;

  /** מחיר סופי רק אחרי תשלום ואישור — לא בשלב הצעה */
  const PROPOSAL_PRICE_DISCLAIMER_HE =
    "המחיר המוצג נכון לשלב זה, ועשוי להשתנות עד להשלמת התשלום ואישור ההזמנה.";

  const handleGoHome = () => {
    if (isAgentDashboardBankTransferSuccessMode) {
      logBankTransferSuccessCtaTrace({
        orderId: bankTransferSuccessCta.orderId,
        bank_transfer: bankTransferSuccessCta.bank_transfer,
        paid: bankTransferSuccessCta.paid,
        pending: bankTransferSuccessCta.pending,
        visibleButtons: bankTransferSuccessCta.visibleButtons,
        hiddenPaymentCtas: bankTransferSuccessCta.hiddenPaymentCtas,
        homeButtonTarget: AGENT_DASHBOARD_HOME_PATH,
        action: "click_home",
      });
      navigate(AGENT_DASHBOARD_HOME_PATH);
      return;
    }
    if (isAppLoggedIn?.()) {
      navigate("/p/dashboard/home");
    } else {
      navigate("/");
    }
  };

  const handleContinueToCheckout = () => {
    navigate(`${location.pathname}?checkout=1`);
  };

  const handleProceedToPayment = async () => {
    console.warn("[PENDING_OFFER_CONTINUE_CLICK]", {
      orderId:
        pendingOfferPaymentContext?.orderId ??
        orderReqData?.id ??
        orderReqData?.ID ??
        orderReqData?.orderid ??
        null,
      hasExternalHandler: typeof onProceedToPayment === "function",
      hasPaymentLinkUrl: !!paymentLinkUrl,
      isProposalFlow,
      customerQuoteOffer,
    });
    if (typeof onProceedToPayment === "function") {
      try {
        const handled = await onProceedToPayment();
        if (handled === true) {
          return;
        }
      } catch (err) {
        console.warn("[PENDING_OFFER_PAYMENT_ERROR]", {
          reason: "onProceedToPayment_exception",
          error: err?.message || String(err),
        });
      }
    }
    if (paymentLinkUrl) {
      window.open(paymentLinkUrl, "_blank", "noopener,noreferrer");
    } else {
      handleContinueToCheckout();
    }
  };

  /** כפתור ראשי — לא כפול מול רצועת העברה בנקאית כשיש לינק; לא במסך success של דשבורד סוכן */
  const showPrimaryPaymentButton =
    isProposalFlow &&
    !isAgentDashboardBankTransferSuccessMode &&
    (customerQuoteOffer || !(isBankTransferRail && paymentLinkUrl));

  useEffect(() => {
    if (!isAgentDashboardBankTransferSuccessMode) return;
    logBankTransferSuccessCtaTrace({
      orderId: bankTransferSuccessCta.orderId,
      bank_transfer: bankTransferSuccessCta.bank_transfer,
      paid: bankTransferSuccessCta.paid,
      pending: bankTransferSuccessCta.pending,
      visibleButtons: bankTransferSuccessCta.visibleButtons,
      hiddenPaymentCtas: bankTransferSuccessCta.hiddenPaymentCtas,
      homeButtonTarget: bankTransferSuccessCta.homeButtonTarget,
    });
  }, [
    isAgentDashboardBankTransferSuccessMode,
    bankTransferSuccessCta.orderId,
    bankTransferSuccessCta.bank_transfer,
    bankTransferSuccessCta.paid,
    bankTransferSuccessCta.pending,
    bankTransferSuccessCta.visibleButtons,
    bankTransferSuccessCta.hiddenPaymentCtas,
    bankTransferSuccessCta.homeButtonTarget,
  ]);

  const statusTextRaw = customerQuoteOffer
    ? "הצעת מחיר אישית"
    : isAgentDashboardBankTransferSuccessMode && bankAwaitingPayment
      ? LDS_BANK_TRANSFER_SUCCESS_TITLE_HE
    : bankAwaitingPayment && bankTransferSubmittedReceipt
      ? t("packageBankTransferReceipt.capturedTitle")
    : bankAwaitingPayment
      ? LDS_BANK_TRANSFER_SUCCESS_TITLE_HE
    : allowSuccessHeader === false
      ? "הצעה בתהליך הזמנה"
      : t("successorder.isapproved") || "ההזמנה נקלטה";

  const statusSubTextRaw = customerQuoteOffer
    ? "להלן פירוט החבילה והמחיר. להשלמת הזמנה יש להמשיך לתשלום."
    : isAgentDashboardBankTransferSuccessMode && bankAwaitingPayment
      ? LDS_BANK_TRANSFER_SUCCESS_SUBTITLE_HE
    : bankAwaitingPayment && bankTransferSubmittedReceipt
      ? t("packageBankTransferReceipt.body")
    : bankAwaitingPayment
      ? LDS_BANK_TRANSFER_SUCCESS_SUBTITLE_HE
    : allowSuccessHeader === false
      ? "ההזמנה טרם הושלמה. לאחר תשלום ואישור תוצג כאן אישור מלא."
      : t("successorder.yourorderto") || "ההזמנה התקבלה במערכת";

  const statusText =
    typeof statusHeaderTitleOverride === "string" && statusHeaderTitleOverride.trim()
      ? statusHeaderTitleOverride.trim()
      : statusTextRaw;

  const statusSubText = (() => {
    if (typeof statusHeaderSubtitleOverride === "string" && statusHeaderSubtitleOverride.trim()) {
      return statusHeaderSubtitleOverride.trim();
    }
    return statusSubTextRaw;
  })();

  const headerVariant = customerQuoteOffer
    ? "offer"
    : bankAwaitingPayment
      ? "pending"
    : allowSuccessHeader === false
      ? "offer"
      : "success";

  const adults = getDetail(details, "סה״כ מבוגרים");
  const kids = getDetail(details, "סה״כ ילדים");
  const checkIn = getDetail(details, "כניסה");
  const checkOut = getDetail(details, "עזיבה");
  const nights = resolveNightsDisplay(details);
  const totalPrice = getDetail(details, "עלות ההזמנה");

  const heroName =
    typeof heroDisplayName === "string" && heroDisplayName.trim()
      ? heroDisplayName.trim()
      : hotelname;

  const displayRating = rating != null && rating !== "" ? String(rating) : "8.5";
  const priceNum = Number(totalPrice);
  const displayPrice =
    !Number.isNaN(priceNum) && totalPrice !== ""
      ? `₪${Math.round(priceNum).toLocaleString("he-IL")}`
      : totalPrice;
  const roomsCount = roomsCountProp != null && roomsCountProp > 0 ? Number(roomsCountProp) : 1;
  const pricePerRoomNum = roomsCount > 0 && !Number.isNaN(priceNum) ? priceNum / roomsCount : null;
  const displayPricePerRoom =
    pricePerRoomNum != null && !Number.isNaN(pricePerRoomNum)
      ? `₪${Math.round(pricePerRoomNum).toLocaleString("he-IL")}`
      : null;
  const roomNames = Array.isArray(roomNamesList) && roomNamesList.length > 0
    ? roomNamesList
    : (roomType ? [roomType] : []);
  const roomOccupancies = buildRoomOccupancies(orderReqData, roomsCount, roomNames);
  const totalPriceUsdNum = Number(totalPriceUsd);
  const hasUsdPrice =
    totalPriceUsd != null &&
    totalPriceUsd !== "" &&
    Number.isFinite(totalPriceUsdNum) &&
    totalPriceUsdNum > 0;
  const displayPriceUsd = hasUsdPrice ? `$${formatUsdDisplayInteger(totalPriceUsdNum)}` : null;
  const passengersToShow =
    passengersList?.length > 0
      ? passengersList
      : fullname && fullname !== "-"
        ? [{ name: fullname, type: "adult" }]
        : [];

  const packageFlightsRouteHeading =
    flightDetails && Array.isArray(flightDetails) && flightDetails.length > 0
      ? buildPackageRouteHeading(flightDetails)
      : null;

  const checkInFormatted = formatDateWithHebrewWeekday(checkIn);
  const checkOutFormatted = formatDateWithHebrewWeekday(checkOut);
  const displayDates =
    checkIn !== "-" && checkOut !== "-"
      ? `${checkInFormatted} – ${checkOutFormatted}`
      : checkIn !== "-"
        ? checkInFormatted
        : checkOutFormatted;

  const showCityTaxNote = hasCityTax === true;
  const orderOurPrice = orderReqData?.ourprice ?? orderReqData?.ourPrice ?? {};
  const selectedBaggageTotalNum = Number(baggageDetails?.selectedPaidTotal ?? NaN);
  const baggageOrderTotalNum = Number(
    orderOurPrice?.baggagePrice ?? orderOurPrice?.baggage_price ?? NaN
  );
  const showBaggageBlock =
    baggageDetails &&
    Array.isArray(baggageDetails.labelLines || baggageDetails.items) &&
    (baggageDetails.labelLines || baggageDetails.items).length > 0;
  const showIncludedInPriceNote =
    showBaggageBlock &&
    ((Number.isFinite(baggageOrderTotalNum) && baggageOrderTotalNum > 0) ||
      (Number.isFinite(selectedBaggageTotalNum) && selectedBaggageTotalNum > 0));
  const baggageLabelLines = React.useMemo(() => {
    const raw = baggageDetails?.labelLines || baggageDetails?.items || [];
    return raw
      .map((line) =>
        String(line || "")
          .replace(/\s+—\s*₪[\d,]+(?:\.\d+)?/g, "")
          .trim()
      )
      .filter(Boolean);
  }, [baggageDetails]);
  const isRoundtripBaggage = baggageLabelLines.some((line) => /הלוך\s*חזור/i.test(String(line)));
  const baggageUnitTypeLabel = baggageLabelLines.some((line) => /טרולי/i.test(String(line)))
    ? "טרולי"
    : "כבודה";
  const baggagePerDirectionNum =
    Number.isFinite(selectedBaggageTotalNum) && selectedBaggageTotalNum > 0
      ? selectedBaggageTotalNum
      : NaN;
  const resolvedBaggageOrderTotalNum =
    Number.isFinite(baggageOrderTotalNum) && baggageOrderTotalNum > 0
      ? baggageOrderTotalNum
      : Number.isFinite(selectedBaggageTotalNum) && selectedBaggageTotalNum > 0
        ? selectedBaggageTotalNum
        : NaN;
  const baggagePerDirectionText =
    isRoundtripBaggage &&
    Number.isFinite(baggagePerDirectionNum) &&
    baggagePerDirectionNum > 0
      ? `עלות ${baggageUnitTypeLabel} לכיוון: ₪${Math.round(baggagePerDirectionNum).toLocaleString("he-IL")}`
      : "";
  const baggageOrderTotalText =
    Number.isFinite(resolvedBaggageOrderTotalNum) && resolvedBaggageOrderTotalNum > 0
      ? `סה״כ עלות להזמנה כבודה: ₪${Math.round(resolvedBaggageOrderTotalNum).toLocaleString("he-IL")}`
      : "";
  const orderSummaryBaggageTraceKey = useDeepCompareMemo(
    () => ({
      showBaggageBlock,
      baggageDetails,
      baggageLabelLines,
    }),
    [showBaggageBlock, baggageDetails, baggageLabelLines]
  );

  const lastOrderSummaryBaggageTraceRef = React.useRef(null);
  React.useEffect(() => {
    const { showBaggageBlock: showBlock, baggageDetails: bd, baggageLabelLines: lines } =
      orderSummaryBaggageTraceKey;
    const payload = {
      showBaggageBlock: showBlock,
      labelLines: showBlock ? lines : [],
      checkedBagsCount: bd?.checkedBagsCount ?? 0,
      trolleyCount: bd?.trolleyCount ?? 0,
      carryOnCount: bd?.carryOnCount ?? 0,
      totalBaggagePrice: bd?.totalBaggagePrice ?? 0,
      isIncludedInTotalPrice: bd?.isIncludedInTotalPrice ?? false,
      selectedPaidLines: bd?.selectedPaidLines ?? [],
      includedLines: bd?.includedLines ?? [],
      selectedPaidTotal: bd?.selectedPaidTotal ?? 0,
    };
    if (isEqual(lastOrderSummaryBaggageTraceRef.current, payload)) return;
    lastOrderSummaryBaggageTraceRef.current = payload;

    safeOrderSummaryTrace("[ORDER_SUMMARY_BAGGAGE_RENDER_DECISION]", {
      shouldRender: showBlock,
      labelLines: showBlock ? lines : [],
      checkedBagsCount: bd?.checkedBagsCount ?? 0,
      trolleyCount: bd?.trolleyCount ?? 0,
      carryOnCount: bd?.carryOnCount ?? 0,
      totalBaggagePrice: bd?.totalBaggagePrice ?? 0,
      isIncludedInTotalPrice: bd?.isIncludedInTotalPrice ?? false,
    });
    if (!showBlock && (bd?.totalBaggagePrice ?? 0) > 0) {
      safeOrderSummaryTrace("[ORDER_SUMMARY_BAGGAGE_MISSING_BUT_PRICE_CHANGED]", {
        totalBaggagePrice: bd?.totalBaggagePrice ?? 0,
      });
    }
    safeOrderSummaryTrace("[ORDER_SUMMARY_SELECTED_BAGGAGE_RENDERED]", {
      shouldRender: showBlock,
      selectedPaidLines: bd?.selectedPaidLines ?? [],
      includedLines: bd?.includedLines ?? [],
      selectedPaidTotal: bd?.selectedPaidTotal ?? 0,
    });
  }, [orderSummaryBaggageTraceKey]);

  const branding = customerQuoteAgencyBranding;
  const companyFromApi =
    branding?.companyName && String(branding.companyName).trim()
      ? String(branding.companyName).trim()
      : null;
  /** בבלוק הזהות ובמקומות שדורשים שם — עם fallback כללי */
  const displayAgencyName = companyFromApi || "סוכנות הנסיעות";
  const hasAgencyLogo = !!(branding?.logoUrl && String(branding.logoUrl).trim());

  const receiptOrderIdRaw =
    orderReqData?.id ?? orderReqData?.ID ?? orderReqData?.orderid ?? null;
  const receiptOrderIdTechnical =
    receiptOrderIdRaw != null && String(receiptOrderIdRaw).trim() !== ""
      ? String(receiptOrderIdRaw).trim()
      : null;
  const receiptOrderIdDisplay = receiptOrderIdTechnical
    ? resolveOrganizedTourDisplayOrderNumber({
        order: orderReqData,
        technicalOrderId: receiptOrderIdTechnical,
        ourPrice: orderOurPrice,
        selectedResult: null,
      }).displayNumber
    : null;
  const receiptOrderId = receiptOrderIdTechnical;
  const packageProfileOrderPath = receiptOrderIdTechnical
    ? `/packages/profile/${receiptOrderIdTechnical}`
    : "/packages/profile";
  const packageOrdersListPath = isAppLoggedIn?.()
    ? ordersDashboardCategoryPath("packages")
    : "/orders/packages";

  const resolvedCustomIntro =
    typeof customerQuoteIntroText === "string" && customerQuoteIntroText.trim()
      ? customerQuoteIntroText
          .trim()
          .replace(/\{\{agencyName\}\}/g, displayAgencyName)
          .replace(/\[שם הסוכנות\]/g, displayAgencyName)
      : null;
  const showAgencyFooter =
    customerQuoteOffer &&
    branding &&
    (branding.companyName ||
      branding.contactName ||
      branding.phone ||
      branding.email);

  const customerOfferSharePageUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}${location.pathname}${location.search}`
      : "";

  const customerOfferWhatsAppShareHref = React.useMemo(() => {
    if (!customerQuoteOffer || !customerOfferSharePageUrl) return null;
    const message = buildCustomerOfferWhatsAppShareMessage({
      greetingName: customerQuoteGreetingName,
      hotelName: heroName,
      offerNumber: customerQuoteOfferNumber,
      priceLabel: displayPrice,
      shareUrl: customerOfferSharePageUrl,
    });
    return buildWhatsAppShareUrl(message);
  }, [
    customerQuoteOffer,
    customerOfferSharePageUrl,
    customerQuoteGreetingName,
    heroName,
    customerQuoteOfferNumber,
    displayPrice,
  ]);

  if (loading) return null;

  return (
    <div className="order-summary" dir="rtl">
      {customerQuoteOffer && (
        <section className="order-summary__customer-quote-top" aria-label="הקדמה">
          <div className="order-summary__customer-quote-body">
            <p className="order-summary__customer-quote-greeting">
              {customerQuoteGreetingName && String(customerQuoteGreetingName).trim()
                ? `שלום ${String(customerQuoteGreetingName).trim()},`
                : "שלום"}
            </p>

            {hasAgencyLogo ? (
              <div
                className="order-summary__customer-quote-agency-logo-wrap"
                aria-label={`לוגו ${displayAgencyName}`}
              >
                <img
                  src={branding.logoUrl}
                  alt=""
                  className="order-summary__customer-quote-agency-logo"
                  decoding="async"
                />
              </div>
            ) : null}

            {resolvedCustomIntro ? (
              <p className="order-summary__customer-quote-intro order-summary__customer-quote-intro--custom">
                {resolvedCustomIntro}
              </p>
            ) : (
              <div className="order-summary__customer-quote-paragraphs">
                <p className="order-summary__customer-quote-intro">
                  הצעת המחיר הוכנה עבורכם על ידי
                  <br />
                  {companyFromApi ? (
                    <>
                      סוכנות הנסיעות{" "}
                      <span className="order-summary__customer-quote-agency-inline">
                        {companyFromApi}
                      </span>
                    </>
                  ) : (
                    "סוכנות הנסיעות"
                  )}
                </p>
                <p className="order-summary__customer-quote-intro">
                  ההצעה מותאמת לפרטים שנמסרו ומשקפת את האפשרויות הזמינות נכון למועד הבדיקה,
                  וניתן לבצע בה התאמות ושינויים לפי הצורך.
                </p>
                <p className="order-summary__customer-quote-intro">
                  לכל שאלה או הבהרה, נשמח לעמוד לרשותכם.
                </p>
              </div>
            )}

            {customerOfferWhatsAppShareHref ? (
              <div className="order-summary__whatsapp-share-wrap">
                <a
                  href={customerOfferWhatsAppShareHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="order-summary__whatsapp-share-btn"
                >
                  <img
                    src={WhatsAppIcon}
                    alt=""
                    className="order-summary__whatsapp-share-icon"
                    width={22}
                    height={22}
                    decoding="async"
                  />
                  <span>שתף הצעה בוואטסאפ</span>
                </a>
              </div>
            ) : null}
          </div>
        </section>
      )}

      {/* 1. HEADER — Status */}
      <header
        className={`order-summary__header order-summary__header--${headerVariant}`}
      >
        <span className="order-summary__header-icon">
          {customerQuoteOffer ? (
            <BsFileEarmarkText aria-hidden />
          ) : bankAwaitingPayment ? (
            <BsClockHistory aria-hidden />
          ) : allowSuccessHeader === false ? (
            <BsFileEarmarkText aria-hidden />
          ) : (
            <BsCheckCircleFill aria-hidden />
          )}
        </span>
        <div className="order-summary__header-text">
          <h1 className="order-summary__header-title">{statusText}</h1>
          {statusSubText ? (
            <p className="order-summary__header-subtitle">{statusSubText}</p>
          ) : null}
          {(isAgentDashboardBankTransferSuccessMode || bankTransferSubmittedReceipt) &&
          bankAwaitingPayment ? (
            <span className="order-summary__bank-pending-badge">
              {LDS_BANK_TRANSFER_PENDING_BADGE_HE}
            </span>
          ) : null}
          {bankTransferSubmittedReceipt && bankAwaitingPayment && receiptOrderId ? (
            <div className="order-summary__receipt-meta">
              <span className="order-summary__receipt-meta-badge">
                {t("packageBankTransferReceipt.statusBadge")}
              </span>
              <p className="order-summary__receipt-meta-order">
                <span className="order-summary__receipt-meta-label">
                  {t("packageBankTransferReceipt.orderNumberLabel")}
                </span>{" "}
                <strong className="order-summary__receipt-meta-id" translate="no">
                  {receiptOrderIdDisplay || receiptOrderId}
                </strong>
              </p>
            </div>
          ) : null}
        </div>
      </header>

      <div className="order-summary__main">
        {/* 2. HERO — Hotel image, name, rating, location, dates */}
        <section className="order-summary__hero">
          <div className="order-summary__hero-image-wrap">
            {hotelMainImage ? (
              <img
                src={hotelMainImage}
                alt={heroName}
                className="order-summary__hero-image"
                onError={(event) => {
                  console.warn("[PROFILE_HOTEL_IMAGE_LOAD_ERROR]", {
                    attemptedSrc: event?.currentTarget?.src || hotelMainImage,
                    hotelname: heroName,
                  });
                  if (customerQuoteOffer) {
                    console.warn("[PROFILE_OFFER_IMAGE_LOAD_ERROR]", {
                      attemptedSrc: event?.currentTarget?.src || hotelMainImage,
                      hotelname: heroName,
                    });
                  }
                }}
              />
            ) : (
              <div className="order-summary__hero-image-placeholder">
                <BsBuilding aria-hidden />
              </div>
            )}
          </div>
          <div className="order-summary__hero-content">
            <h2 className="order-summary__hero-name">{heroName}</h2>
            {hotelCountry && hotelCountry !== "-" && (
              <p className="order-summary__hero-location">
                <BsGeoAlt className="order-summary__hero-icon" />
                {hotelCountry}
              </p>
            )}
            {showHeroStarRating ? (
              <div className="order-summary__hero-rating">
                <BsStarFill className="order-summary__hero-star" />
                <span>{displayRating}</span>
                <span className="order-summary__hero-rating-label">מצוין</span>
              </div>
            ) : null}
            {(checkIn !== "-" || checkOut !== "-") && (
              <p className="order-summary__hero-dates">
                <BsCalendar3 className="order-summary__hero-icon" />
                {displayDates}
              </p>
            )}
            {customerQuoteOffer &&
              customerQuoteOfferNumber &&
              String(customerQuoteOfferNumber).trim() && (
                <p className="order-summary__offer-number" aria-label="מספר הצעה">
                  <span className="order-summary__offer-number-label">מספר הצעה</span>
                  <span className="order-summary__offer-number-value">
                    {String(customerQuoteOfferNumber).trim()}
                  </span>
                </p>
              )}
          </div>
        </section>

        {/* 3. ROOM DETAILS — room names and/or type, count, price per room */}
        {!hideRoomDetailsSection ? (
          <section className="order-summary__card order-summary__rooms">
            <h3 className="order-summary__card-title">
              <BsMoon className="order-summary__card-icon" />
              פירוט חדרים
            </h3>
            <ul className="order-summary__summary-list">
              {roomsCount > 1 ? (
                roomNames.slice(0, roomsCount).map((name, i) => (
                  <li key={i}>
                    <span className="order-summary__summary-label">חדר {i + 1}</span>
                    <span className="order-summary__summary-value order-summary__summary-value--room">
                      <span className="order-summary__room-name">{name}</span>
                      <span className="order-summary__room-occupancy">
                        {`הרכב: ${roomOccupancies[i] || "—"}`}
                      </span>
                    </span>
                  </li>
                ))
              ) : (
                <>
                  <li>
                    <span className="order-summary__summary-label">מספר חדרים</span>
                    <span className="order-summary__summary-value">{roomsCount}</span>
                  </li>
                  <li>
                    <span className="order-summary__summary-label">סוג חדר / שם החדר</span>
                    <span className="order-summary__summary-value order-summary__summary-value--room">
                      <span className="order-summary__room-name">
                        {roomNames[0] || roomType || "חדר סטנדרטי"}
                      </span>
                      <span className="order-summary__room-occupancy">
                        {`הרכב: ${roomOccupancies[0] || "—"}`}
                      </span>
                    </span>
                  </li>
                  {displayPricePerRoom && (
                    <li>
                      <span className="order-summary__summary-label">מחיר לחדר</span>
                      <span className="order-summary__summary-value">{displayPricePerRoom}</span>
                    </li>
                  )}
                </>
              )}
            </ul>
          </section>
        ) : null}

        <div className="order-summary__grid">
          <div className="order-summary__summary-flights-row">
            {/* 4. SUMMARY — nights, guests, board */}
            <section className="order-summary__card order-summary__summary">
              <h3 className="order-summary__card-title">
                <BsMoon className="order-summary__card-icon" />
                {packageFactsSectionTitle || "פרטי החבילה"}
              </h3>
              <div className="order-summary__card-body">
                <ul className="order-summary__summary-list">
                  {getDetail(details, "יעד") !== "-" ? (
                    <li>
                      <span className="order-summary__summary-label">יעד</span>
                      <span className="order-summary__summary-value">
                        {getDetail(details, "יעד")}
                      </span>
                    </li>
                  ) : null}
                  <li>
                    <span className="order-summary__summary-label">מספר לילות</span>
                    <span className="order-summary__summary-value">{nights}</span>
                  </li>
                  {packageGuestsSingleLine && String(packageGuestsSingleLine).trim() ? (
                    <li>
                      <span className="order-summary__summary-label">נוסעים</span>
                      <span className="order-summary__summary-value">
                        {String(packageGuestsSingleLine).trim()}
                      </span>
                    </li>
                  ) : (
                    <>
                      <li>
                        <span className="order-summary__summary-label">מבוגרים</span>
                        <span className="order-summary__summary-value">{adults}</span>
                      </li>
                      <li>
                        <span className="order-summary__summary-label">ילדים</span>
                        <span className="order-summary__summary-value">{kids}</span>
                      </li>
                    </>
                  )}
                  {getDetail(details, "ימים (לפי תאריכים)") !== "-" ? (
                    <li>
                      <span className="order-summary__summary-label">ימים (לפי תאריכים)</span>
                      <span className="order-summary__summary-value">
                        {getDetail(details, "ימים (לפי תאריכים)")}
                      </span>
                    </li>
                  ) : null}
                  {board && board !== "-" && (
                    <li>
                      <span className="order-summary__summary-label">בסיס אירוח</span>
                      <span className="order-summary__summary-value">{board}</span>
                    </li>
                  )}
                  {getDetail(details, "ספק") !== "-" ? (
                    <li>
                      <span className="order-summary__summary-label">ספק</span>
                      <span className="order-summary__summary-value">
                        {getDetail(details, "ספק")}
                      </span>
                    </li>
                  ) : null}
                  {showBaggageBlock && (
                    <li>
                      <span className="order-summary__summary-label">כבודה</span>
                      <span className="order-summary__summary-value order-summary__summary-value--room">
                        <span className="order-summary__room-name">{baggageLabelLines.join(" + ")}</span>
                        {baggagePerDirectionText ? (
                          <span className="order-summary__room-occupancy">
                            {baggagePerDirectionText}
                          </span>
                        ) : null}
                        {baggageOrderTotalText ? (
                          <span className="order-summary__room-occupancy">
                            {baggageOrderTotalText}
                          </span>
                        ) : null}
                        {showIncludedInPriceNote ? (
                          <span className="order-summary__room-occupancy">
                            כבודה זו כלולה במחיר הכולל
                          </span>
                        ) : null}
                      </span>
                    </li>
                  )}
                </ul>
              </div>
            </section>

            {/* Flight details section */}
            {flightDetails && Array.isArray(flightDetails) && flightDetails.length > 0 && (
              <section className="order-summary__card order-summary__flights" dir="rtl">
                <h3 className="order-summary__card-title">
                  <BsAirplane className="order-summary__card-icon" />
                  פרטי טיסה
                </h3>
                <div className="order-summary__flight-details order-summary__flight-details--fpc order-summary__card-body">
                  <div className="fpc order-summary__fpc-embed" dir="rtl">
                    {packageFlightsRouteHeading ? (
                      <p className="fpc__route-heading" dir="ltr" translate="no">
                        {packageFlightsRouteHeading}
                      </p>
                    ) : null}
                    <div className="fpc__itinerary">
                      <div className="fpc__legs-stack">
                        {flightDetails.map((flight, idx) => (
                          <FlightLegBlock
                            key={flight.flightCode ?? `f-${idx}`}
                            leg={mapPackageFlightDetailToFpcLeg(flight, idx, flightDetails.length)}
                            legIndex={idx}
                            showDivider={idx > 0}
                            showSupplierInMeta={isAdminUser()}
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </section>
            )}
          </div>

          {customerQuoteOffer &&
          (publicOfferIncludedTransfersVm || publicOfferIncludedBaggageVm) ? (
            <div className="order-summary__included-offer-benefits-slot">
              {publicOfferIncludedTransfersVm ? (
                <PublicOfferIncludedTransfersCard
                  vm={publicOfferIncludedTransfersVm}
                />
              ) : null}
              {publicOfferIncludedBaggageVm &&
              (publicOfferIncludedBaggageVm.hasIncluded || !showBaggageBlock) ? (
                <PublicOfferIncludedBaggageCard
                  vm={publicOfferIncludedBaggageVm}
                />
              ) : null}
            </div>
          ) : null}

          {includedAttractionsSlot ? (
            <div className="order-summary__included-attractions-slot">{includedAttractionsSlot}</div>
          ) : null}

          {/* 5. PRICE BOX — total, no commission display */}
          <section className="order-summary__card order-summary__price-box">
            <h3 className="order-summary__card-title">
              <BsCreditCard className="order-summary__card-icon" />
              {customerQuoteOffer
                ? "מחיר לפי הצעה"
                : isPackagesProfileDedicatedSuccessPath
                  ? "סה״כ לתשלום"
                  : isProposalFlow
                    ? "מחיר נוכחי להצעה"
                    : "סה״כ לתשלום"}
            </h3>
            <div className="order-summary__price-main">
              {displayPrice}
            </div>
            {baggagePerDirectionText && (
              <p className="order-summary__price-note" aria-label="עלות כבודה לכיוון">
                {baggagePerDirectionText}
              </p>
            )}
            {baggageOrderTotalText && (
              <p className="order-summary__price-note" aria-label="שורת כבודה">
                {baggageOrderTotalText}
              </p>
            )}
            {displayPriceUsd && (
              <p className="order-summary__price-usd" aria-label="מחיר בדולר">
                ({displayPriceUsd})
              </p>
            )}
            {customerQuoteOffer && customerQuotePriceDisclaimer && (
              <p className="order-summary__price-disclaimer-usd">{customerQuotePriceDisclaimer}</p>
            )}
            <p className="order-summary__price-includes">
              {customerQuoteOffer
                ? "המחיר בהצעה זו — ההזמנה תושלם רק לאחר אישורך ותשלום."
                : isProposalFlow
                  ? bankAwaitingPayment
                    ? organizedTourPriceFootnote
                      ? `${PROPOSAL_PRICE_DISCLAIMER_HE} לאחר זיהוי ההעברה הבנקאית יימשך אישור ההזמנה אצל הספק.`
                      : `${PROPOSAL_PRICE_DISCLAIMER_HE} לאחר זיהוי ההעברה הבנקאית יימשך אישור ההזמנה.`
                    : PROPOSAL_PRICE_DISCLAIMER_HE
                  : organizedTourPriceFootnote
                    ? "המחיר המוצג נכון להזמנה זו. ייתכנו חיובים מקומיים נוספים לפי כללי היעד והספק."
                    : "המחיר המוצג הינו סופי להזמנה זו, למעט חיובים מקומיים אפשריים שישולמו ישירות למלון"}
            </p>
            {showCityTaxNote && !organizedTourPriceFootnote && (
              <p className="order-summary__price-local-tax">
                ייתכן חיוב מס מקומי שישולם ישירות במלון
              </p>
            )}
            {showCityTaxNote && organizedTourPriceFootnote ? (
              <p className="order-summary__price-local-tax">
                ייתכנו חיובים מקומיים לפי כללי היעד
              </p>
            ) : null}
            {!customerQuoteOffer && !isProposalFlow && (
              <p className="order-summary__price-trust">
                המחיר המוצג הינו סופי להזמנה זו
              </p>
            )}
            <p className="order-summary__price-note">
              {customerQuoteOffer
                ? "טרם בוצעה הזמנה סופית"
                : bankAwaitingPayment
                  ? "ממתין להעברה בנקאית"
                  : isProposalFlow
                    ? "ההזמנה טרם הושלמה — ניתן להמשיך לתשלום"
                    : "ההזמנה אושרה"}
            </p>
          </section>

          {(isAgentDashboardBankTransferSuccessMode ||
            (bankTransferSubmittedReceipt && bankAwaitingPayment)) && (
            <LdsBankTransferDetailsBlock />
          )}
        </div>

        {/* CTA תשלום / המשך תהליך — הצעה (?offer=1) או "הצעה בתהליך הזמנה"; לא כפול כשיש רצועת העברה בנקאית עם לינק */}
        {showPrimaryPaymentButton && (
          <section className="order-summary__offer-cta" aria-label="המשך לתשלום">
            <button
              type="button"
              className="order-summary__offer-btn"
              onClick={handleProceedToPayment}
            >
              המשך לתשלום
            </button>
          </section>
        )}

        {customerQuoteOffer && (
          <LdsCustomerQuoteLegalSection
            additionalInfoHtml={customerQuoteAdditionalInfoHtml}
            showFullTermsLink
            className="order-summary__customer-quote-legal"
          />
        )}

        {paymentLinkUrl && isBankTransferRail && !customerQuoteOffer && isProposalFlow && !isAgentDashboardBankTransferSuccessMode && (
          <section className="order-summary__quick-pay" aria-label="תשלום מהיר">
            <div className="order-summary__quick-pay-inner">
              <BsCreditCard className="order-summary__quick-pay-icon" aria-hidden />
              <div className="order-summary__quick-pay-text">
                <p className="order-summary__quick-pay-title">רוצה לאשר את ההזמנה מהר יותר?</p>
                <a
                  href={paymentLinkUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="order-summary__quick-pay-link"
                >
                  שלם באשראי באמצעות לינק לתשלום מאובטח
                </a>
              </div>
            </div>
          </section>
        )}

        {/* Trust icons row */}
        <section className="order-summary__trust-icons">
          <span className="order-summary__trust-item">
            <BsShieldLock className="order-summary__trust-icon" aria-hidden />
            תשלום מאובטח
          </span>
          <span className="order-summary__trust-item">
            <BsCheck2Circle className="order-summary__trust-icon" aria-hidden />
            {customerQuoteOffer ? "הצעה אישית" : isProposalFlow ? "לפני השלמת הזמנה" : "אישור מיידי"}
          </span>
          <span className="order-summary__trust-item">
            <BsClock className="order-summary__trust-icon" aria-hidden />
            שמירת מחיר לזמן מוגבל
          </span>
        </section>

        {/* 5. PASSENGERS — Cards / text only */}
        {passengersToShow.length > 0 && (
          <section className="order-summary__card order-summary__passengers">
            <h3 className="order-summary__card-title">
              <BsPeople className="order-summary__card-icon" />
              נוסעים
            </h3>
            <div className="order-summary__passengers-list">
              {passengersToShow.map((p, i) => (
                <div key={i} className="order-summary__passenger-card">
                  <BsPerson className="order-summary__passenger-icon" />
                  <span className="order-summary__passenger-name">{p.name}</span>
                  {p.type && (
                    <span className="order-summary__passenger-type">
                      {p.type === "child" ? "ילד" : "מבוגר"}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Status / thank you line or Home button */}
        {isAgentDashboardBankTransferSuccessMode ? (
          <div className="order-summary__home-row">
            <button
              type="button"
              className="order-summary__home-btn order-summary__home-btn--primary"
              onClick={handleGoHome}
              aria-label={bankTransferSuccessCta.homeButtonLabel || "עמוד הבית"}
            >
              <BsHouse className="order-summary__home-btn-icon" />
              {bankTransferSuccessCta.homeButtonLabel || "עמוד הבית"}
            </button>
          </div>
        ) : bankTransferSubmittedReceipt && bankAwaitingPayment && !customerQuoteOffer ? (
          <div className="order-summary__receipt-actions" aria-label={t("packageBankTransferReceipt.actionsAria")}>
            <button
              type="button"
              className="order-summary__receipt-btn order-summary__receipt-btn--primary"
              onClick={() => navigate(packageProfileOrderPath)}
            >
              {t("packageBankTransferReceipt.viewOrder")}
            </button>
            <button
              type="button"
              className="order-summary__receipt-btn order-summary__receipt-btn--secondary"
              onClick={() => navigate(packageOrdersListPath)}
            >
              {t("packageBankTransferReceipt.ordersArea")}
            </button>
            <a
              href={packageOrdersListPath}
              className="order-summary__receipt-link"
            >
              {t("packageBankTransferReceipt.trackStatus")}
            </a>
            <button
              type="button"
              className="order-summary__receipt-btn order-summary__receipt-btn--ghost"
              onClick={() => window.print()}
            >
              {t("packageBankTransferReceipt.printOrSavePdf")}
            </button>
          </div>
        ) : isBankTransferRail && !customerQuoteOffer ? (
          <div className="order-summary__home-row">
            <button
              type="button"
              className="order-summary__home-btn order-summary__home-btn--primary"
              onClick={handleGoHome}
              aria-label="עמוד הבית"
            >
              <BsHouse className="order-summary__home-btn-icon" />
              עמוד הבית
            </button>
          </div>
        ) : customerQuoteOffer ? null : (
          fullname && fullname !== "-" && (
            <p className="order-summary__thanks">
              {t("successorder.thankyou") || "תודה"}, {fullname}!
            </p>
          )
        )}

        {showAgencyFooter ? <CustomerQuoteAgencyContactFooter branding={branding} /> : null}
      </div>
    </div>
  );
};

export default OrderSummary;
